import { createContext, useCallback, useContext, useMemo } from "react";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import {
  useListUserCurrentTrackerValuesFunctionRequest,
  useSetUserTrackerGoalFunctionRequest,
  useSetUserTrackerValueFunctionRequest,
  useSetUserTrackerValuesFunctionRequest
} from "shared/request/myHealthyAdvantageApi";
import { toTrackerType, TrackerType } from "../Trackers/TrackerType";
import { CurrentHealthTrackerData, getCurrentHealthData, getHealthTrackerData } from "core/javascriptChannels";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { useTranslation } from "react-i18next";
import { convertMinutesToHours } from "core/helpers/time";
import { isSameDay } from "core/helpers/dates";

const defaultGoals: Record<TrackerType, number> = {
  [TrackerType.Water]: 2000,
  [TrackerType.Steps]: 10_000,
  [TrackerType.Sleep]: 8,
  [TrackerType.Activity]: 30,
  [TrackerType.BreathingExercises]: 0,
};
export type TrackerContextType = {
  isLoading: boolean;
  hasError: boolean;
  getCurrentTrackerValue: (trackerType: TrackerType) => number;
  getGoal: (trackerType: TrackerType) => number;
  setTrackerData: (trackerType: TrackerType, value: number) => void;
  setTrackerGoal: (trackerType: TrackerType, goal: number) => void;
  refreshCurrentTrackerData: () => void;
  fetchTrackerDataFromDate: (trackerType: TrackerType, dateFrom: Date) => void;
};

export const TrackerContext = createContext({} as TrackerContextType);

export const useTrackerContext = () => useContext(TrackerContext);

export const TrackerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const xClientCode = ClientInfoState.getCode();
  const { t } = useTranslation(undefined, { keyPrefix: "wellbeing.trackers" });
  const { modalNotify } = useNotifyModalContext();
  const { trigger: setTrackerValueTrigger } = useSetUserTrackerValueFunctionRequest({ xClientCode });
  const { trigger: setTrackerValuesTrigger } = useSetUserTrackerValuesFunctionRequest({ xClientCode });
  const { trigger: setTrackerGoalTrigger } = useSetUserTrackerGoalFunctionRequest({ xClientCode });
  const {
    data: trackersData,
    mutate: fetchTrackerValues,
    isLoading,
    error,
  } = useListUserCurrentTrackerValuesFunctionRequest({
    xClientCode,
  });

  const setTrackerData = useCallback(
    async (trackerType: TrackerType, value: number) => {
      try {
        await setTrackerValueTrigger({
          body: {
            trackerType,
            date: new Date().toDateOnly(),
            value,
            manual: true,
          },
        });
        await fetchTrackerValues();
      } catch {
        modalNotify("error", t("setCurrentErrorTitle"), t("setCurrentError"), t("errorCta"));
      }
    },
    [modalNotify, fetchTrackerValues, setTrackerValueTrigger, t]
  );

  const setTrackerGoal = useCallback(
    async (trackerType: TrackerType, goal: number) => {
      try {
        await setTrackerGoalTrigger({ body: { trackerType, goal } });
        await fetchTrackerValues();
      } catch {
        modalNotify("error", t("setGoalErrorTitle"), t("setGoalError"), t("errorCta"));
      }
    },
    [modalNotify, fetchTrackerValues, setTrackerGoalTrigger, t]
  );

  const getTrackerData = useCallback(
    (trackerType: TrackerType) =>
      trackersData?.find((data) => data.trackerType === trackerType) ?? { trackerType, value: 0, goal: defaultGoals[trackerType] },
    [trackersData]
  );

  const parseValue = (trackerType: TrackerType, value: number) => (trackerType === TrackerType.Sleep ? convertMinutesToHours(value) : value);

  const getCurrentTrackerValue = useCallback(
    (trackerType: TrackerType) => parseValue(trackerType, getTrackerData(trackerType)?.value ?? 0),
    [getTrackerData]
  );

  const getGoal = useCallback(
    (trackerType: TrackerType) => (!getTrackerData(trackerType)?.goal ? defaultGoals[trackerType] : getTrackerData(trackerType)?.goal),
    [getTrackerData]
  );

  const refreshCurrentTrackerData = useCallback(async () => {
    try {
      const healthData = await getCurrentHealthData();

      if (!!healthData) {
        const trackerValuesToUpdate = healthData.filter((data) => !!data.value && isSameDay(new Date(data.date), new Date()));

        const trackers = trackerValuesToUpdate.map((trackerData: CurrentHealthTrackerData) => ({
          trackerType: toTrackerType(trackerData.trackerType),
          date: new Date(trackerData.date).toDateOnly(),
          value: trackerData.value,
        }));

        await setTrackerValuesTrigger({
          body: {
            trackers
          },
        })
      }
      await fetchTrackerValues();
    } catch (error) {
      console.error("Error sending tracker data:", error);
    }
  }, [fetchTrackerValues, setTrackerValuesTrigger]);

  const fetchTrackerDataFromDate = useCallback(
    async (trackerType: TrackerType, dateFrom: Date) => {
      try {
        const healthData = await getHealthTrackerData(TrackerType[trackerType].toLowerCase(), dateFrom.toDateOnlyString());

        if (!!healthData && healthData?.length > 0) {
          const trackerValuesToUpdate = healthData.filter((data) => !!data.value && new Date(data.date).getTime() >= dateFrom.getTime());
          const trackers = trackerValuesToUpdate.map((trackerData: CurrentHealthTrackerData) => ({
            trackerType: toTrackerType(trackerData.trackerType),
            date: new Date(trackerData.date).toDateOnly(),
            value: trackerData.value,
          }));
          await setTrackerValuesTrigger({
            body: {
              trackers
            },
          });
        }
        await fetchTrackerValues();
      } catch (error) {
        console.error("Error sending tracker data:", error);
      }
    },
    [fetchTrackerValues, setTrackerValuesTrigger]
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      hasError: !!error,
      getCurrentTrackerValue,
      getGoal,
      setTrackerData,
      setTrackerGoal,
      refreshCurrentTrackerData,
      fetchTrackerDataFromDate,
    }),
    [isLoading, error, getCurrentTrackerValue, getGoal, setTrackerData, setTrackerGoal, refreshCurrentTrackerData, fetchTrackerDataFromDate]
  );

  return <TrackerContext.Provider value={contextValue}>{children}</TrackerContext.Provider>;
};
